import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/hooks/useAuth';
import ErrorBoundary from '../../shared/components/ErrorBoundary';
import Footer from '../../shared/components/Footer';
import PageLoader from '../../shared/components/PageLoader';
import { PATHS } from './paths';

const LoginPage = lazy(() => import('../../features/auth/pages/LoginPage'));
const SignupPage = lazy(() => import('../../features/auth/pages/SignupPage'));
const DashboardPage = lazy(() => import('../../features/dashboard/pages/DashboardPage'));
const CalendarPage = lazy(() => import('../../features/dashboard/pages/CalendarPage'));
const ActivityHubPage = lazy(() => import('../../features/dashboard/pages/ActivityHubPage'));
const QuizzesPage = lazy(() => import('../../features/dashboard/pages/QuizzesPage'));
const SettingsPage = lazy(() => import('../../features/dashboard/pages/SettingsPage'));
const PricingPage = lazy(() => import('../../features/dashboard/pages/PricingPage'));
const IntegrationCallbackPage = lazy(() =>
    import('../../features/integrations/pages/IntegrationCallbackPage')
);
const PrivacyPolicyPage = lazy(() => import('../../features/legal/pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() =>
    import('../../features/legal/pages/TermsOfServicePage')
);
const LandingPage = lazy(() => import('../../features/landing/LandingPage'));
const AdminLayoutOutlet = lazy(() => import('../../features/admin/features/layout/AdminLayoutOutlet'));
const AdminOverviewPage = lazy(() => import('../../features/admin/features/overview/AdminOverviewPage'));
const AdminPlansPage = lazy(() => import('../../features/admin/features/plans/AdminPlansPage'));
const AdminUsersPage = lazy(() => import('../../features/admin/features/users/AdminUsersPage'));
const AdminSchedulePage = lazy(() => import('../../features/admin/features/schedule/AdminSchedulePage'));
const AdminAttendancePage = lazy(() => import('../../features/admin/features/attendance/AdminAttendancePage'));
const AdminQuizzesPage = lazy(() => import('../../features/admin/features/quizzes/AdminQuizzesPage'));
function AppRoutesInner() {
    const { user, loading } = useAuth();
    const { pathname } = useLocation();
    const isAuthScreen = pathname === PATHS.login || pathname === PATHS.signup;
    const isLanding = pathname === PATHS.landing && !user;

    if (loading) return <PageLoader />;

    const shellStyle = {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        ...(isAuthScreen
            ? {
                  height: '100vh',
                  maxHeight: '100dvh',
                  overflow: 'hidden',
              }
            : { minHeight: '100vh', overflowX: 'hidden' }),
    };

    const mainStyle = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        ...(isAuthScreen ? { minHeight: 0, overflow: 'hidden' } : {}),
    };

    return (
        <div style={shellStyle}>
            <div style={mainStyle}>
                <Routes>
                    <Route
                        path={PATHS.landing}
                        element={user ? <Navigate to={PATHS.dashboard} replace /> : <LandingPage />}
                    />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms" element={<TermsOfServicePage />} />
                    <Route
                        path={PATHS.login}
                        element={!user ? <LoginPage /> : <Navigate to={PATHS.dashboard} replace />}
                    />
                    <Route
                        path={PATHS.signup}
                        element={!user ? <SignupPage /> : <Navigate to={PATHS.dashboard} replace />}
                    />
                    <Route
                        path={PATHS.dashboard}
                        element={user ? <DashboardPage /> : <Navigate to={PATHS.login} />}
                    />
                    <Route
                        path={PATHS.calendar}
                        element={user ? <CalendarPage /> : <Navigate to={PATHS.login} />}
                    />
                    <Route
                        path={PATHS.activityHub}
                        element={user ? <ActivityHubPage /> : <Navigate to={PATHS.login} />}
                    />
                    <Route path="/summaries" element={<Navigate to={PATHS.activityHub} replace />} />
                    <Route
                        path={PATHS.quizzes}
                        element={user ? <QuizzesPage /> : <Navigate to={PATHS.login} />}
                    />
                    <Route path="/transcripts" element={<Navigate to={PATHS.quizzes} replace />} />
                    <Route
                        path={PATHS.pricing}
                        element={user ? <PricingPage /> : <Navigate to={PATHS.login} />}
                    />
                    <Route
                        path={PATHS.settings}
                        element={user ? <SettingsPage /> : <Navigate to={PATHS.login} />}
                    />
                    <Route
                        path={PATHS.integrationsCallback}
                        element={user ? <IntegrationCallbackPage /> : <Navigate to={PATHS.login} />}
                    />
                    <Route
                        path="/admin"
                        element={user ? <AdminLayoutOutlet /> : <Navigate to={PATHS.login} />}
                    >
                        <Route index element={<Navigate to="overview" replace />} />
                        <Route path="overview" element={<AdminOverviewPage />} />
                        <Route path="plans" element={<AdminPlansPage />} />
                        <Route path="users" element={<AdminUsersPage />} />
                        <Route path="schedule" element={<AdminSchedulePage />} />
                        <Route path="quizzes" element={<AdminQuizzesPage />} />
                        <Route path="attendance" element={<AdminAttendancePage />} />
                    </Route>
                    <Route
                        path="*"
                        element={<Navigate to={user ? PATHS.dashboard : PATHS.login} replace />}
                    />
                </Routes>
            </div>
            {!isAuthScreen && !isLanding ? <Footer /> : null}
        </div>
    );
}

const AppRoutes = () => (
    <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
            <AppRoutesInner />
        </Suspense>
    </ErrorBoundary>
);

export default AppRoutes;
